import qs from "qs";
import {useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {Facility} from "../../store/reporting/reporting.common";
import {
  CustomDateRangePreset,
  findDateRangePreset,
} from "../../atoms/reporting-compare";
import {useReportContext} from "./ReportContext";
import {
  parseReportingListPath,
  reportingListPathWithDateRange,
} from "./reporting-paths";
import {
  getReportingSectionMeta,
  ReportingListCategory,
} from "./reporting-menu/helpers";
import {getWorkflowLabel} from "../../atoms/start-workflow/workflowMapping";
import {useChecklistItems} from "./reporting-menu/useChecklistItems";

type Props = Readonly<{
  from1?: string;
  to1?: string;
}>;

export const ReportingListUrlSync = ({from1, to1}: Props): null => {
  const location = useLocation();
  const navigate = useNavigate();
  const {data: checklistItems = []} = useChecklistItems();
  const {
    baseDateRange,
    setBaseDateRange,
    setActiveFacilities,
    setSelectedWorkflowLabel,
  } = useReportContext();
  const prevUrlDatesRef = useRef<{from1?: string; to1?: string} | null>(null);

  useEffect(() => {
    if (!location.search) {
      return;
    }

    const {facilities} = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });

    if (facilities) {
      setActiveFacilities({
        checkedKeys: facilities as Facility[],
        checkedLeafKeys: facilities as Facility[],
      });
    }

    navigate(
      {pathname: location.pathname, search: ""},
      {replace: true, state: location.state},
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!from1 || !to1) {
      return;
    }

    // Always keep URL from/to. Preset match only supplies id/label.
    const activeDateRange = findDateRangePreset(from1, to1) || {
      ...CustomDateRangePreset,
      from: from1,
      to: to1,
    };

    setBaseDateRange({
      ...activeDateRange,
      from: from1,
      to: to1,
    });
  }, [from1, to1, setBaseDateRange]);

  useEffect(() => {
    const prevUrlDates = prevUrlDatesRef.current;
    const urlDatesChanged =
      prevUrlDates !== null &&
      (prevUrlDates.from1 !== from1 || prevUrlDates.to1 !== to1);
    prevUrlDatesRef.current = {from1, to1};

    const listPath = parseReportingListPath(location.pathname);

    if (!listPath || !baseDateRange.from || !baseDateRange.to) {
      return;
    }

    if (from1 && to1) {
      const stateBehindUrl =
        baseDateRange.from !== from1 || baseDateRange.to !== to1;
      const urlIsSourceOfTruth =
        urlDatesChanged || prevUrlDates === null;

      if (urlIsSourceOfTruth && stateBehindUrl) {
        return;
      }
    }

    const pathname = reportingListPathWithDateRange(
      listPath.category,
      listPath.sectionId,
      baseDateRange.from,
      baseDateRange.to,
    );

    if (pathname !== location.pathname) {
      navigate({pathname, search: ""}, {replace: true, state: location.state});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDateRange, from1, to1]);

  // Keep the breadcrumb's workflow label in sync with the active section.
  useEffect(() => {
    const listPath = parseReportingListPath(location.pathname);

    if (!listPath) {
      setSelectedWorkflowLabel(null);
      return;
    }

    const meta = getReportingSectionMeta(
      listPath.sectionId,
      listPath.category as ReportingListCategory,
      checklistItems,
    );

    setSelectedWorkflowLabel(meta ? getWorkflowLabel(meta.workflowType) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, checklistItems]);

  return null;
};
